import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type {
  DayId,
  DaypartRule,
  HolidayRule,
  LimitedRunWindow,
  MenuAvailabilityRules,
} from '@/types/menuAvailability';

const DAY_ORDER: DayId[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<DayId, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

const zeroRules: MenuAvailabilityRules = {
  dayparts: [],
  holidays: [],
  limitedRuns: [],
};

const toMinuteValue = (value: string): number | null => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
};

const formatMinuteValue = (value: number | null): string => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return '';
  }
  const hours = Math.floor(value / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (value % 60).toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatLimitedRun = (window: LimitedRunWindow): string => {
  const start = new Date(window.start);
  const startLabel = start.toLocaleString();
  if (!window.end) {
    return `${startLabel} → (open)`;
  }
  const end = new Date(window.end);
  return `${startLabel} → ${end.toLocaleString()}`;
};

const createId = (prefix: string) => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}`;
};

const cloneRules = (rules?: MenuAvailabilityRules): MenuAvailabilityRules => ({
  timezone: rules?.timezone,
  dayparts: (rules?.dayparts ?? []).map((entry) => ({ ...entry, days: [...entry.days] })),
  holidays: (rules?.holidays ?? []).map((entry) => ({ ...entry })),
  limitedRuns: (rules?.limitedRuns ?? []).map((entry) => ({ ...entry })),
});

interface AvailabilityEditorProps {
  value?: MenuAvailabilityRules | null;
  onChange: (next: MenuAvailabilityRules) => void;
  timezone?: string | null;
}

export function AvailabilityEditor({ value, onChange, timezone }: AvailabilityEditorProps) {
  const rules = useMemo(
    () => (value ? cloneRules(value) : cloneRules(zeroRules)),
    [value],
  );
  const fallbackTimezone = useMemo(() => {
    if (timezone && timezone.trim().length > 0) {
      return timezone;
    }
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
      return null;
    }
  }, [timezone]);

  const [isDaypartFormOpen, setDaypartFormOpen] = useState(false);
  const [isHolidayFormOpen, setHolidayFormOpen] = useState(false);
  const [isLimitedRunFormOpen, setLimitedRunFormOpen] = useState(false);

  const [daypartDraft, setDaypartDraft] = useState<{
    label: string;
    days: Set<DayId>;
    start: string;
    end: string;
  }>({
    label: '',
    days: new Set<DayId>(),
    start: '09:00',
    end: '17:00',
  });

  const [holidayDraft, setHolidayDraft] = useState<{
    label: string;
    date: string;
    isClosed: boolean;
    start: string;
    end: string;
  }>({
    label: '',
    date: '',
    isClosed: true,
    start: '00:00',
    end: '23:59',
  });

  const [limitedRunDraft, setLimitedRunDraft] = useState<{
    label: string;
    start: string;
    end: string;
  }>({
    label: '',
    start: '',
    end: '',
  });

  const updateRules = useCallback(
    (updater: (current: MenuAvailabilityRules) => MenuAvailabilityRules) => {
      const next = updater(cloneRules(rules));
      if (!next.timezone && fallbackTimezone) {
        next.timezone = fallbackTimezone;
      }
      onChange(next);
    },
    [fallbackTimezone, onChange, rules],
  );

  const handleAddDaypart = useCallback(() => {
    const startMinute = toMinuteValue(daypartDraft.start);
    const endMinute = toMinuteValue(daypartDraft.end);
    const selectedDays = Array.from(daypartDraft.days);

    if (!selectedDays.length || startMinute === null || endMinute === null || endMinute <= startMinute) {
      return;
    }

    const entry: DaypartRule = {
      id: createId('daypart'),
      label: daypartDraft.label.trim() || undefined,
      days: selectedDays,
      startMinute,
      endMinute,
    };

    updateRules((current) => ({
      ...current,
      dayparts: [...current.dayparts, entry],
    }));

    setDaypartDraft({
      label: '',
      days: new Set<DayId>(),
      start: '09:00',
      end: '17:00',
    });
    setDaypartFormOpen(false);
  }, [daypartDraft, updateRules]);

  const handleRemoveDaypart = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      updateRules((current) => ({
        ...current,
        dayparts: current.dayparts.filter((entry) => entry.id !== id),
      }));
    },
    [updateRules],
  );

  const handleAddHoliday = useCallback(() => {
    if (!holidayDraft.date) {
      return;
    }

    const startMinute = holidayDraft.isClosed ? null : toMinuteValue(holidayDraft.start);
    const endMinute = holidayDraft.isClosed ? null : toMinuteValue(holidayDraft.end);

    if (!holidayDraft.isClosed && (startMinute === null || endMinute === null || endMinute <= startMinute)) {
      return;
    }

    const entry: HolidayRule = {
      id: createId('holiday'),
      label: holidayDraft.label.trim() || undefined,
      date: holidayDraft.date,
      isClosed: holidayDraft.isClosed,
      startMinute: holidayDraft.isClosed ? undefined : startMinute ?? undefined,
      endMinute: holidayDraft.isClosed ? undefined : endMinute ?? undefined,
    };

    updateRules((current) => ({
      ...current,
      holidays: [...current.holidays, entry],
    }));

    setHolidayDraft({
      label: '',
      date: '',
      isClosed: true,
      start: '00:00',
      end: '23:59',
    });
    setHolidayFormOpen(false);
  }, [holidayDraft, updateRules]);

  const handleRemoveHoliday = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      updateRules((current) => ({
        ...current,
        holidays: current.holidays.filter((entry) => entry.id !== id),
      }));
    },
    [updateRules],
  );

  const handleAddLimitedRun = useCallback(() => {
    if (!limitedRunDraft.start) {
      return;
    }
    const start = Date.parse(limitedRunDraft.start);
    const end = limitedRunDraft.end ? Date.parse(limitedRunDraft.end) : null;

    if (!Number.isFinite(start) || (end !== null && (!Number.isFinite(end) || end <= start))) {
      return;
    }

    const entry: LimitedRunWindow = {
      id: createId('limited'),
      label: limitedRunDraft.label.trim() || undefined,
      start,
      end,
    };

    updateRules((current) => ({
      ...current,
      limitedRuns: [...current.limitedRuns, entry],
    }));

    setLimitedRunDraft({
      label: '',
      start: '',
      end: '',
    });
    setLimitedRunFormOpen(false);
  }, [limitedRunDraft, updateRules]);

  const handleRemoveLimitedRun = useCallback(
    (id: string | undefined) => {
      if (!id) return;
      updateRules((current) => ({
        ...current,
        limitedRuns: current.limitedRuns.filter((entry) => entry.id !== id),
      }));
    },
    [updateRules],
  );

  const timezoneLabel = (rules.timezone ?? fallbackTimezone)
    ? `Times in ${rules.timezone ?? fallbackTimezone}`
    : 'Times follow restaurant timezone';

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold">Daypart availability</p>
          <p className="text-xs text-muted-foreground">{timezoneLabel}</p>
        </div>
        <div className="space-y-2">
          {rules.dayparts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No dayparts configured. Items follow global hours.</p>
          ) : (
            rules.dayparts.map((entry) => (
              <div
                key={entry.id ?? `${entry.label ?? 'daypart'}-${entry.startMinute}-${entry.endMinute}`}
                className="flex items-center justify-between rounded-md border border-border/50 bg-muted/20 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium">
                    {entry.label ?? 'Daypart'} · {formatMinuteValue(entry.startMinute)} –{' '}
                    {formatMinuteValue(entry.endMinute)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entry.days.map((day) => DAY_LABEL[day]).join(', ')}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive/80"
                  onClick={() => handleRemoveDaypart(entry.id)}
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </div>
        {isDaypartFormOpen ? (
          <div className="rounded-md border border-dashed border-border/70 bg-muted/10 p-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium" htmlFor="daypart-label">
                Label
              </Label>
              <Input
                id="daypart-label"
                placeholder="e.g. Lunch"
                value={daypartDraft.label}
                onChange={(event) => setDaypartDraft((prev) => ({ ...prev, label: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAY_ORDER.map((day) => {
                  const isActive = daypartDraft.days.has(day);
                  return (
                    <Button
                      key={day}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      className={cn('text-xs', isActive ? 'bg-primary text-primary-foreground' : '')}
                      onClick={() =>
                        setDaypartDraft((prev) => {
                          const next = new Set(prev.days);
                          if (next.has(day)) {
                            next.delete(day);
                          } else {
                            next.add(day);
                          }
                          return { ...prev, days: next };
                        })
                      }
                    >
                      {DAY_LABEL[day]}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium" htmlFor="daypart-start">
                  Start time
                </Label>
                <Input
                  id="daypart-start"
                  type="time"
                  value={daypartDraft.start}
                  onChange={(event) => setDaypartDraft((prev) => ({ ...prev, start: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium" htmlFor="daypart-end">
                  End time
                </Label>
                <Input
                  id="daypart-end"
                  type="time"
                  value={daypartDraft.end}
                  onChange={(event) => setDaypartDraft((prev) => ({ ...prev, end: event.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => setDaypartFormOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleAddDaypart}>
                Add daypart
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setDaypartFormOpen(true)}>
            Add daypart
          </Button>
        )}
      </section>

      <Separator />

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Holidays & closures</p>
            <p className="text-xs text-muted-foreground">Override availability on specific dates.</p>
          </div>
        </div>
        <div className="space-y-2">
          {rules.holidays.length === 0 ? (
            <p className="text-xs text-muted-foreground">No holiday overrides configured.</p>
          ) : (
            rules.holidays.map((entry) => (
              <div
                key={entry.id ?? `${entry.date}-${entry.label ?? 'holiday'}`}
                className="flex items-center justify-between rounded-md border border-border/50 bg-muted/20 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium">
                    {entry.label ?? 'Holiday'} · {entry.date}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entry.isClosed ? 'Closed' : `Open ${formatMinuteValue(entry.startMinute ?? null)} – ${formatMinuteValue(entry.endMinute ?? null)}`}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive/80"
                  onClick={() => handleRemoveHoliday(entry.id)}
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </div>
        {isHolidayFormOpen ? (
          <div className="rounded-md border border-dashed border-border/70 bg-muted/10 p-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium" htmlFor="holiday-label">
                Label
              </Label>
              <Input
                id="holiday-label"
                placeholder="e.g. Thanksgiving"
                value={holidayDraft.label}
                onChange={(event) => setHolidayDraft((prev) => ({ ...prev, label: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium" htmlFor="holiday-date">
                  Date
                </Label>
                <Input
                  id="holiday-date"
                  type="date"
                  value={holidayDraft.date}
                  onChange={(event) => setHolidayDraft((prev) => ({ ...prev, date: event.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch
                  id="holiday-closed"
                  checked={holidayDraft.isClosed}
                  onCheckedChange={(checked) => setHolidayDraft((prev) => ({ ...prev, isClosed: checked }))}
                />
                <Label htmlFor="holiday-closed" className="text-xs">
                  Closed all day
                </Label>
              </div>
            </div>
            {!holidayDraft.isClosed && (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium" htmlFor="holiday-start">
                    Opens at
                  </Label>
                  <Input
                    id="holiday-start"
                    type="time"
                    value={holidayDraft.start}
                    onChange={(event) => setHolidayDraft((prev) => ({ ...prev, start: event.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium" htmlFor="holiday-end">
                    Closes at
                  </Label>
                  <Input
                    id="holiday-end"
                    type="time"
                    value={holidayDraft.end}
                    onChange={(event) => setHolidayDraft((prev) => ({ ...prev, end: event.target.value }))}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => setHolidayFormOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleAddHoliday}>
                Add holiday
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setHolidayFormOpen(true)}>
            Add holiday
          </Button>
        )}
      </section>

      <Separator />

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Limited-run windows</p>
            <p className="text-xs text-muted-foreground">Temporarily enable items for special events.</p>
          </div>
        </div>
        <div className="space-y-2">
          {rules.limitedRuns.length === 0 ? (
            <p className="text-xs text-muted-foreground">No limited-run windows configured.</p>
          ) : (
            rules.limitedRuns.map((entry) => (
              <div
                key={entry.id ?? `${entry.start}-${entry.end ?? 'open'}`}
                className="flex items-center justify-between rounded-md border border-border/50 bg-muted/20 px-3 py-2"
              >
                <div>
                  <p className="text-sm font-medium">{entry.label ?? 'Limited run'}</p>
                  <p className="text-xs text-muted-foreground">{formatLimitedRun(entry)}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive/80"
                  onClick={() => handleRemoveLimitedRun(entry.id)}
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </div>
        {isLimitedRunFormOpen ? (
          <div className="rounded-md border border-dashed border-border/70 bg-muted/10 p-3 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium" htmlFor="limited-label">
                Label
              </Label>
              <Input
                id="limited-label"
                placeholder="e.g. Restaurant Week"
                value={limitedRunDraft.label}
                onChange={(event) => setLimitedRunDraft((prev) => ({ ...prev, label: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium" htmlFor="limited-start">
                  Starts at
                </Label>
                <Input
                  id="limited-start"
                  type="datetime-local"
                  value={limitedRunDraft.start}
                  onChange={(event) => setLimitedRunDraft((prev) => ({ ...prev, start: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium" htmlFor="limited-end">
                  Ends at (optional)
                </Label>
                <Input
                  id="limited-end"
                  type="datetime-local"
                  value={limitedRunDraft.end}
                  onChange={(event) => setLimitedRunDraft((prev) => ({ ...prev, end: event.target.value }))}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => setLimitedRunFormOpen(false)}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleAddLimitedRun}>
                Add window
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={() => setLimitedRunFormOpen(true)}>
            Add limited run
          </Button>
        )}
      </section>

    </div>
  );
}
